import React from 'react';

interface SkeletonLoaderProps {
  className?: string;
  variant?: 'text' | 'rectangular' | 'circular';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  lines = 1
}) => {
  const baseClasses = 'animate-pulse bg-gray-200 dark:bg-gray-700 rounded';
  
  if (variant === 'circular') {
    return (
      <div 
        className={`${baseClasses} ${className}`}
        style={{ width: width || '40px', height: height || '40px' }}
      />
    );
  }

  if (variant === 'rectangular') {
    return (
      <div 
        className={`${baseClasses} ${className}`}
        style={{ width: width || '100%', height: height || '20px' }}
      />
    );
  }

  // Text variant with multiple lines
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`${baseClasses} h-4`}
          style={{
            width: index === lines - 1 ? '70%' : '100%', // Last line shorter
          }}
        />
      ))}
    </div>
  );
};

// Dashboard specific skeleton components
export const DashboardStatsSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
        <SkeletonLoader variant="text" lines={2} />
        <div className="mt-4">
          <SkeletonLoader variant="rectangular" height="32px" width="60%" />
        </div>
      </div>
    ))}
  </div>
);

export const RecentOrdersSkeleton: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
    <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
      <SkeletonLoader variant="rectangular" height="24px" width="120px" />
    </div>
    <div className="p-4 sm:p-6 space-y-4">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-center justify-between">
          <div className="flex-1">
            <SkeletonLoader variant="text" lines={1} />
            <SkeletonLoader variant="text" lines={1} className="mt-1" />
          </div>
          <div className="text-right">
            <SkeletonLoader variant="rectangular" height="20px" width="80px" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const PopularPackagesSkeleton: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
    <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
      <SkeletonLoader variant="rectangular" height="24px" width="140px" />
    </div>
    <div className="p-4 sm:p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <SkeletonLoader variant="rectangular" height="20px" width="80%" />
            <SkeletonLoader variant="text" lines={2} className="mt-2" />
            <SkeletonLoader variant="rectangular" height="24px" width="60%" className="mt-3" />
          </div>
        ))}
      </div>
    </div>
  </div>
);
